import equal from "fast-deep-equal";
import type { PropsWithChildren } from "react";
import { useCallback, useMemo, useState } from "react";
import type { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type {
  GroupBasedAuthorization,
  GroupForSharing,
  ReportAuthorization,
  ReportGroup,
  UserBasedAuthorization,
  UserForSharing,
} from "../../../../../../../../../api/types/svTypes.ts";
import type { ReportCompany } from "../ShareReport.types.ts";
import { SharingGroupContext } from "./SharingGroupContext.tsx";

interface Props {
  clientCode: string;
  groupId: string;
  company: ReportCompany;
  currentUserId: string;
  defaultAuthorization: ReportAuthorization;
  users: UserForSharing[];
  groups: GroupForSharing[];
  updateSharing: (
    clientCode: string,
    groupId: string,
    authorization: ReportAuthorization
  ) => Promise<ApiResponse<ReportGroup>>;
  onClose: () => void;
}

export const SharingGroupContextProvider = ({
  clientCode,
  groupId,
  company,
  currentUserId,
  defaultAuthorization,
  users,
  groups,
  updateSharing,
  onClose,
  children,
}: PropsWithChildren<Props>) => {
  const { sendNotificationError } = useNotificationContext();
  const [isSaving, setIsSaving] = useState(false);
  const [companyAccess, setCompanyAccess] = useState<ReportAccessType>(defaultAuthorization.companyShared.access);
  const [authorizedUsers, setAuthorizedUsers] = useState<UserBasedAuthorization[]>(() =>
    structuredClone(defaultAuthorization.users)
  );
  const [authorizedGroups, setAuthorizedGroups] = useState<GroupBasedAuthorization[]>(() =>
    structuredClone(defaultAuthorization.groups)
  );

  const authorization = useMemo<ReportAuthorization>(
    () => ({ users: authorizedUsers, groups: authorizedGroups, companyShared: { access: companyAccess } }),
    [authorizedUsers, authorizedGroups, companyAccess]
  );

  const isChanged = useMemo(() => !equal(authorization, defaultAuthorization), [authorization, defaultAuthorization]);

  const availableNotAuthorizedUsers = useMemo(
    () => users.filter((u) => !authorizedUsers.some((au) => au.userId === u.userId)),
    [users, authorizedUsers]
  );

  const onUsersSelected = useCallback((selected: UserForSharing[], access: ReportAccessType) => {
    setAuthorizedUsers((prev) => [
      ...prev,
      ...selected.map((u): UserBasedAuthorization => ({ userId: u.userId, access })),
    ]);
  }, []);

  const onGroupsSelected = useCallback((selected: GroupForSharing[], access: ReportAccessType) => {
    setAuthorizedGroups((prev) => [
      ...prev,
      ...selected.map((g): GroupBasedAuthorization => ({ groupId: g.groupId, access })),
    ]);
  }, []);

  const onUserAccessChanged = useCallback((userId: string, access: ReportAccessType) => {
    setAuthorizedUsers((prev) => prev.map((u) => (u.userId === userId ? { ...u, access } : u)));
  }, []);

  const onGroupAccessChanged = useCallback((gid: string, access: ReportAccessType) => {
    setAuthorizedGroups((prev) => prev.map((g) => (g.groupId === gid ? { ...g, access } : g)));
  }, []);

  const onRemoveUser = useCallback((userId: string) => {
    setAuthorizedUsers((prev) => prev.filter((u) => u.userId !== userId));
  }, []);

  const onRemoveGroup = useCallback((gid: string) => {
    setAuthorizedGroups((prev) => prev.filter((g) => g.groupId !== gid));
  }, []);

  const onSave = useCallback(async () => {
    if (!isChanged) {
      return;
    }
    setIsSaving(true);
    try {
      const response = await updateSharing(clientCode, groupId, authorization);
      if (response.success) {
        onClose();
      } else {
        sendNotificationError("Failed to update sharing. Please try again.");
      }
    } catch (e) {
      logError(e, "[ShareReportGroup] updateSharing");
      sendNotificationError("Failed to update sharing. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [authorization, clientCode, groupId, isChanged, onClose, sendNotificationError, updateSharing]);

  return (
    <SharingGroupContext
      value={{
        company,
        currentUserId,
        users,
        groups,
        availableNotAuthorizedUsers,
        companyAccess,
        setCompanyAccess,
        authorizedUsers,
        authorizedGroups,
        isChanged,
        isSaving,
        onUsersSelected,
        onGroupsSelected,
        onUserAccessChanged,
        onGroupAccessChanged,
        onRemoveUser,
        onRemoveGroup,
        onSave,
      }}
    >
      {children}
    </SharingGroupContext>
  );
};
